import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiFetch } from '../../api';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatValue(val, band) {
  if (val) return '€' + Number(val).toLocaleString();
  if (band && band !== 'unknown') return band.charAt(0).toUpperCase() + band.slice(1);
  return '—';
}

function isExpired(deadline) {
  if (!deadline) return false;
  return new Date(deadline) < new Date();
}

function tedUrl(tedId) {
  if (!tedId) return null;
  return `https://ted.europa.eu/en/notice/${tedId}`;
}

const s = {
  page: { maxWidth: 980, margin: '32px auto', padding: '0 24px 60px' },
  backBtn: {
    background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, padding: 0, marginBottom: 20,
  },
  heading: { fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 8, lineHeight: 1.3 },
  headingMeta: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 },
  metaBadge: (color, bg) => ({
    fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 12, background: bg, color,
  }),
  tedIdMono: { fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' },

  section: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 16 },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 },

  metaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' },
  metaRow: {
    display: 'flex', flexDirection: 'column', gap: 2,
    padding: '8px 0', borderBottom: '1px solid #f8fafc',
  },
  metaKey: { fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' },
  metaVal: { fontSize: 13, color: '#1e293b', fontWeight: 500 },

  cpvList: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  cpvTag: {
    fontSize: 12, padding: '3px 10px', borderRadius: 20,
    background: '#f1f5f9', color: '#475569', fontFamily: 'monospace',
  },

  actionsRow: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  rerankBtn: (loading) => ({
    padding: '8px 18px',
    background: loading ? '#93c5fd' : '#2563eb',
    color: '#fff', border: 'none', borderRadius: 7,
    fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
  }),
  extLink: {
    padding: '7px 14px', background: '#fff', color: '#374151',
    border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500,
    textDecoration: 'none', display: 'inline-block',
  },
  msg: (ok) => ({ fontSize: 13, fontWeight: 500, color: ok ? '#16a34a' : '#b91c1c' }),

  // Verdict distribution
  distRow: { display: 'flex', gap: 10, marginBottom: 20 },
  distCard: (bg) => ({
    flex: 1, background: bg, borderRadius: 8, padding: '12px 16px', textAlign: 'center',
  }),
  distCount: (color) => ({ fontSize: 24, fontWeight: 700, color }),
  distLabel: (color) => ({ fontSize: 11, marginTop: 3, color }),

  // Match table
  matchTable: { width: '100%', borderCollapse: 'collapse' },
  th: {
    fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
    letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left',
    background: '#f8fafc', borderBottom: '1px solid #e2e8f0',
  },
  td: {
    fontSize: 13, color: '#374151', padding: '12px 14px',
    borderBottom: '1px solid #f1f5f9', verticalAlign: 'top',
  },
  verdictBadge: (verdict) => {
    const cfg = { great: ['#dcfce7', '#15803d'], good: ['#fef9c3', '#854d0e'], maybe: ['#f1f5f9', '#64748b'] };
    const [bg, color] = cfg[verdict] || cfg.maybe;
    return { fontSize: 13, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: bg, color, whiteSpace: 'nowrap', display: 'inline-block' };
  },
  reasoning: { fontSize: 12, color: '#64748b', lineHeight: 1.55, marginTop: 4, maxWidth: 360 },
  userLink: { color: '#2563eb', textDecoration: 'none', fontWeight: 500 },
  tierBadge: { fontSize: 11, color: '#94a3b8', marginTop: 2 },

  emptyMatches: { padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: 14 },
  emptyRerankBtn: {
    marginTop: 10, padding: '7px 16px', background: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
};

export default function AdminTenderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reranking, setReranking] = useState(false);
  const [msg, setMsg] = useState(null);
  const [expandedReasoning, setExpandedReasoning] = useState({});

  useEffect(() => {
    apiFetch(`/admin/tenders/${id}`)
      .then((d) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  async function handleRerank() {
    if (!window.confirm('Re-rank this tender for all active profiles?\nExisting match results will be cleared and re-scored.')) return;
    setReranking(true);
    setMsg(null);
    try {
      const res = await apiFetch(`/admin/tenders/${id}/rerank`, { method: 'POST' });
      setMsg({ text: res.message, ok: true });
    } catch (e) {
      setMsg({ text: e.message, ok: false });
    } finally {
      setReranking(false);
    }
  }

  if (loading) {
    return <div style={s.page}><p style={{ color: '#94a3b8', fontSize: 14 }}>Loading…</p></div>;
  }
  if (!data) {
    return <div style={s.page}><p style={{ color: '#b91c1c', fontSize: 14 }}>Failed to load tender.</p></div>;
  }

  const { tender, matches } = data;
  const expired = isExpired(tender.deadline);

  const great = matches.filter((m) => m.verdict === 'great').length;
  const good = matches.filter((m) => m.verdict === 'good').length;
  const maybe = matches.filter((m) => m.verdict === 'maybe').length;

  const metaFields = [
    { key: 'Deadline', val: formatDate(tender.deadline), color: expired ? '#b91c1c' : undefined },
    { key: 'Contract value', val: formatValue(tender.contract_value, tender.contract_size_band) },
    { key: 'Published (TED)', val: formatDate(tender.publication_date) },
    { key: 'Ingested', val: formatDateTime(tender.created_at) },
    { key: 'Notice type', val: tender.notice_type || '—' },
    { key: 'Contract nature', val: tender.contract_nature || '—' },
    { key: 'Procedure type', val: tender.procedure_type || '—' },
    { key: 'Award criterion', val: tender.award_criterion_type || '—' },
    { key: 'Buyer', val: tender.buyer_name || '—' },
    { key: 'Buyer city', val: tender.buyer_city || '—' },
    { key: 'Buyer type', val: tender.buyer_type || '—' },
    { key: 'Submission language', val: tender.submission_language || '—' },
    { key: 'Framework agreement', val: tender.framework_agreement ? 'Yes' : 'No' },
    { key: 'EU funded', val: tender.eu_funded === true ? 'Yes' : tender.eu_funded === false ? 'No' : '—' },
  ];

  return (
    <div style={s.page}>
      <button style={s.backBtn} onClick={() => navigate('/admin/tenders')}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        Back to tenders
      </button>

      {/* Header */}
      <h1 style={s.heading}>{tender.title || 'Untitled tender'}</h1>
      <div style={s.headingMeta}>
        <span style={s.metaBadge('#1d4ed8', '#dbeafe')}>{tender.country || '—'}</span>
        {expired && <span style={s.metaBadge('#b91c1c', '#fee2e2')}>Expired</span>}
        {!expired && tender.deadline && (
          <span style={{ fontSize: 13, color: '#64748b' }}>Deadline {formatDate(tender.deadline)}</span>
        )}
        {tender.ted_id && <span style={s.tedIdMono}>{tender.ted_id}</span>}
        <span style={{ fontSize: 13, color: '#64748b' }}>
          {matches.length} match{matches.length !== 1 ? 'es' : ''}
        </span>
      </div>

      {/* Actions */}
      <div style={{ ...s.section, padding: '14px 20px' }}>
        <div style={s.actionsRow}>
          <button style={s.rerankBtn(reranking)} onClick={handleRerank} disabled={reranking}>
            {reranking ? 'Queuing…' : 'Re-rank for all profiles'}
          </button>
          {tender.document_url && (
            <a href={tender.document_url} target="_blank" rel="noopener noreferrer" style={s.extLink}>
              Documents ↗
            </a>
          )}
          {tender.submission_url && (
            <a href={tender.submission_url} target="_blank" rel="noopener noreferrer" style={s.extLink}>
              Submission portal ↗
            </a>
          )}
          {tedUrl(tender.ted_id) && (
            <a href={tedUrl(tender.ted_id)} target="_blank" rel="noopener noreferrer" style={s.extLink}>
              TED notice ↗
            </a>
          )}
          {msg && <span style={s.msg(msg.ok)}>{msg.text}</span>}
        </div>
      </div>

      {/* Metadata grid */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Details</div>
        <div style={s.metaGrid}>
          {metaFields.map(({ key, val, color }) => (
            <div key={key} style={s.metaRow}>
              <span style={s.metaKey}>{key}</span>
              <span style={{ ...s.metaVal, ...(color ? { color } : {}) }}>{val}</span>
            </div>
          ))}
        </div>
      </div>

      {/* CPV codes */}
      {tender.cpv_codes?.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>CPV codes ({tender.cpv_codes.length})</div>
          <div style={s.cpvList}>
            {tender.cpv_codes.map((c) => (
              <span key={c} style={s.cpvTag}>{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Matches */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Matches ({matches.length})</div>

        {/* Score distribution */}
        {matches.length > 0 && (
          <div style={s.distRow}>
            <div style={s.distCard('#dcfce7')}>
              <div style={s.distCount('#15803d')}>{great}</div>
              <div style={s.distLabel('#15803d')}>Great fit</div>
            </div>
            <div style={s.distCard('#fef9c3')}>
              <div style={s.distCount('#854d0e')}>{good}</div>
              <div style={s.distLabel('#854d0e')}>Good fit</div>
            </div>
            <div style={s.distCard('#f1f5f9')}>
              <div style={s.distCount('#64748b')}>{maybe}</div>
              <div style={s.distLabel('#64748b')}>Maybe</div>
            </div>
          </div>
        )}

        {matches.length === 0 ? (
          <div style={s.emptyMatches}>
            <div>No matches yet for this tender.</div>
            <button style={s.emptyRerankBtn} onClick={handleRerank} disabled={reranking}>
              {reranking ? 'Queuing…' : 'Re-rank now'}
            </button>
          </div>
        ) : (
          <table style={s.matchTable}>
            <thead>
              <tr>
                <th style={s.th}>User</th>
                <th style={s.th}>Profile / Company</th>
                <th style={s.th}>Verdict</th>
                <th style={s.th}>Reasoning</th>
                <th style={s.th}>Matched</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m, i) => {
                const profile = m.company_profiles || {};
                const user = m.users || {};
                const reasoning = m.claude_reasoning || '';
                const expanded = expandedReasoning[i];
                const truncated = reasoning.length > 130 && !expanded;

                return (
                  <tr key={i}>
                    <td style={s.td}>
                      {user.email ? (
                        <Link
                          to={`/admin/users/${m.user_id}`}
                          style={s.userLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {user.email}
                        </Link>
                      ) : (
                        <span style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 12 }}>
                          {m.user_id?.slice(0, 8)}…
                        </span>
                      )}
                      {user.subscription_tier && (
                        <div style={s.tierBadge}>
                          {user.subscription_tier} · {user.subscription_status}
                        </div>
                      )}
                    </td>
                    <td style={s.td}>
                      <div style={{ fontWeight: 500, color: '#1e293b' }}>
                        {profile.profile_name || profile.company_name || '—'}
                      </div>
                      {profile.profile_name && profile.company_name &&
                        profile.profile_name !== profile.company_name && (
                          <div style={{ fontSize: 12, color: '#94a3b8' }}>{profile.company_name}</div>
                        )}
                    </td>
                    <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                      <span style={s.verdictBadge(m.verdict)}>
                        {{ great: 'Great fit', good: 'Good fit', maybe: 'Maybe' }[m.verdict] || m.verdict}
                      </span>
                    </td>
                    <td style={s.td}>
                      {reasoning ? (
                        <>
                          <div style={s.reasoning}>
                            {truncated ? reasoning.slice(0, 130) + '…' : reasoning}
                          </div>
                          {reasoning.length > 130 && (
                            <button
                              onClick={() => setExpandedReasoning((prev) => ({ ...prev, [i]: !expanded }))}
                              style={{
                                background: 'none', border: 'none', color: '#2563eb',
                                fontSize: 12, cursor: 'pointer', padding: 0, marginTop: 4,
                              }}
                            >
                              {expanded ? 'Show less' : 'Show more'}
                            </button>
                          )}
                        </>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...s.td, fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                      {formatDate(m.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
